
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { type Service } from "@/components/services/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, Phone, Mail, ArrowLeft, MessageSquare, Calendar as CalendarIcon, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { ContactFormDialog } from "@/components/services/ContactFormDialog";
import { MessageDialog } from "@/components/services/MessageDialog";
import { AppointmentRequestDialog } from "@/components/services/AppointmentRequestDialog";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { preloadCriticalImages } from "@/utils/imageOptimizer";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

const MobileServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isAppointmentDialogOpen, setIsAppointmentDialogOpen] = useState(false);

  console.log("MobileServiceDetails - Rendering with ID:", id);
  console.log("User session:", session?.user?.id);

  const { data: service, isLoading } = useQuery({
    queryKey: ["service", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("professional_services")
        .select(`
          *,
          profiles:professional_id (
            full_name,
            profile_photo,
            rating,
            response_rate,
            response_time,
            total_reviews,
            about_me,
            experience,
            education,
            languages,
            certifications
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as Service;
    },
  });

  const handlePrevImage = () => {
    if (!service?.photos) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? service.photos!.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    if (!service?.photos) return;
    setCurrentImageIndex((prev) => 
      prev === service.photos!.length - 1 ? 0 : prev + 1
    );
  };

  const handleContactClick = () => {
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour contacter le professionnel");
      return;
    }

    if (!service?.professional_id) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    console.log("Opening contact dialog for professional:", service.professional_id);
    setIsContactDialogOpen(true);
  };

  const handleMessageClick = () => {
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour envoyer un message");
      return;
    }

    if (!service?.professional_id) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    console.log("Opening message dialog for professional:", service.professional_id);
    setIsMessageDialogOpen(true);
  };

  const handleAppointmentClick = () => {
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour prendre rendez-vous");
      return;
    }

    if (!service?.professional_id) {
      toast.error("Erreur: ID du professionnel manquant");
      return;
    }

    console.log("Opening appointment dialog for service:", service.id);
    setIsAppointmentDialogOpen(true);
  };

  // Log pour déboguer
  useEffect(() => {
    if (service) {
      console.log("Service détails chargés:", service);
      console.log("Images disponibles:", service.photos || [service.photo_url].filter(Boolean));
      console.log("Professional ID:", service.professional_id);
    }
  }, [service]);

  // Préchargement des images pour une navigation plus fluide
  useEffect(() => {
    if (service?.photos && service.photos.length > 0) {
      // Précharge l'image courante et les deux suivantes
      const imagesToPreload = [service.photos[currentImageIndex]];
      
      if (service.photos.length > 1) {
        const nextIndex = (currentImageIndex + 1) % service.photos.length;
        imagesToPreload.push(service.photos[nextIndex]);
        
        if (service.photos.length > 2) {
          const nextNextIndex = (currentImageIndex + 2) % service.photos.length;
          imagesToPreload.push(service.photos[nextNextIndex]);
        }
      }
      
      preloadCriticalImages(imagesToPreload);
    } else if (service?.photo_url) {
      preloadCriticalImages([service.photo_url]);
    }
  }, [service, currentImageIndex]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-background p-4 border-b">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Skeleton className="h-6 w-1/2 inline-block" />
        </div>
        <div className="space-y-4 p-4">
          <Skeleton className="h-72 w-full rounded-lg" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!service) return null;

  const images = service.photos?.length > 0 
    ? service.photos 
    : (service.photo_url ? [service.photo_url] : []);
  
  const currentImage = images.length > 0 ? images[currentImageIndex] : null;

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm p-4 border-b flex items-center">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(-1)}
          className="mr-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-lg font-semibold truncate">{service.service_name}</h1>
      </div>

      {/* Main content */}
      <div className="space-y-6">
        {/* Image gallery */}
        <div className="relative">
          {currentImage ? (
            <div className="aspect-[4/3] bg-muted">
              <OptimizedImage 
                src={currentImage} 
                alt={service.service_name} 
                className="w-full h-full object-cover"
                priority={true}
              />
              {images.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full h-8 w-8"
                    onClick={handlePrevImage}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white rounded-full h-8 w-8"
                    onClick={handleNextImage}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {images.map((_, idx) => (
                      <div 
                        key={idx} 
                        className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="aspect-[4/3] bg-muted flex items-center justify-center">
              <span className="text-muted-foreground">Pas d'image disponible</span>
            </div>
          )}
        </div>

        {/* Service info */}
        <div className="px-4 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">{service.service_name}</h2>
              <Badge>{service.price}€</Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{service.duration} minutes</span>
            </div>
          </div>

          {/* Professional info */}
          <div className="flex items-center gap-3 py-3 border-t border-b">
            <Avatar className="h-12 w-12">
              {service.profiles?.profile_photo ? (
                <AvatarImage src={service.profiles.profile_photo} alt={service.profiles?.full_name || ''} />
              ) : (
                <AvatarFallback>
                  {service.profiles?.full_name?.charAt(0) || 'P'}
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <div className="font-medium">{service.profiles?.full_name || 'Professionnel'}</div>
              {service.profiles?.rating && (
                <div className="flex items-center gap-1 text-sm">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  <span>{service.profiles.rating.toFixed(1)}</span>
                  <span className="text-muted-foreground">
                    ({service.profiles.total_reviews || 0} avis)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <h3 className="font-medium">Description</h3>
            <p className="text-sm text-muted-foreground">
              {service.description || "Aucune description fournie pour ce service."}
            </p>
          </div>

          {/* Contact info */}
          {(service.address || service.email || service.phone) && (
            <div className="space-y-2">
              <h3 className="font-medium">Contact</h3>
              <div className="space-y-1.5">
                {service.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{service.address}</span>
                  </div>
                )}
                {service.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4" />
                    <span>{service.phone}</span>
                  </div>
                )}
                {service.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4" />
                    <span>{service.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Professional details */}
          {service.profiles?.about_me && (
            <div className="space-y-2">
              <h3 className="font-medium">À propos du professionnel</h3>
              <p className="text-sm text-muted-foreground">
                {service.profiles.about_me}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t flex gap-3">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={handleMessageClick}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Contacter
        </Button>
        <Button 
          className="flex-1"
          onClick={handleAppointmentClick}
        >
          <CalendarIcon className="h-4 w-4 mr-2" />
          Prendre RDV
        </Button>
      </div>

      <ContactFormDialog
        open={isContactDialogOpen}
        onOpenChange={setIsContactDialogOpen}
        serviceTitle={service.service_name}
        serviceId={service.id}
        professionalId={service.professional_id}
      />

      <MessageDialog
        open={isMessageDialogOpen}
        onOpenChange={setIsMessageDialogOpen}
        professionalId={service.professional_id}
        professionalName={service.profiles?.full_name || ""}
        context="service"
        itemTitle={service.service_name}
      />

      <AppointmentRequestDialog
        open={isAppointmentDialogOpen}
        onOpenChange={setIsAppointmentDialogOpen}
        serviceId={service.id}
        professionalId={service.professional_id}
        serviceName={service.service_name}
      />
    </div>
  );
};

export default MobileServiceDetails;
